/**
 * Ponte para o shell nativo (Tauri). Ver docs/10-desktop.md.
 *
 * A web app NÃO depende do Tauri. O shell injecta `window.__TASCA_NATIVE__`
 * antes de a página correr e isto é só a interface tipada do que lá está —
 * duck typing de propósito:
 *
 *  - o bundle da web não cresce com um SDK que 99% dos utilizadores não usa;
 *  - a mesma build serve browser e desktop, sem `#ifdef`;
 *  - e o código que lê isto compila e corre sem o Tauri instalado, o que
 *    mantém `pnpm dev` e os testes a funcionar sem toolchain de Rust.
 *
 * O preço é que o contrato existe em dois sítios (aqui e em
 * `apps/desktop/src/prelude.ts`) e tem de ser mantido à mão. Vale: a
 * alternativa era a web app importar `@tauri-apps/api`.
 */

export interface NativeBridge {
  platform: 'windows' | 'macos' | 'linux';
  /** Versão do shell, não da web app. */
  version: string;

  /**
   * Refresh token no keychain do SO.
   *
   * No browser isto é um cookie HttpOnly que o JS nunca vê. Numa app nativa
   * não há cookie jar que sobreviva, e o equivalente honesto é o cofre do
   * sistema — Credential Manager, Keychain, Secret Service.
   */
  secrets: {
    get(): Promise<string | null>;
    set(token: string): Promise<void>;
    clear(): Promise<void>;
  };

  notify(options: { title: string; body: string }): Promise<void>;

  /** Badge no ícone / tooltip do tray. */
  setUnread(count: number): Promise<void>;

  autostart: {
    isEnabled(): Promise<boolean>;
    set(enabled: boolean): Promise<void>;
  };

  /**
   * Push-to-talk GLOBAL — a razão de existir da app de desktop.
   *
   * Um browser só recebe teclas com a janela em foco, o que torna o
   * push-to-talk inútil durante um jogo ou uma apresentação. Isto registra a
   * tecla no SO.
   */
  pushToTalk: {
    /** `false` se o SO recusar o atalho (já tomado por outra app). */
    bind(accelerator: string): Promise<boolean>;
    unbind(): Promise<void>;
    /** Devolve a função de cancelamento. */
    onChange(callback: (down: boolean) => void): () => void;
  };
}

declare global {
  interface Window {
    __TASCA_NATIVE__?: NativeBridge;
  }
}

export const native: NativeBridge | null =
  typeof window !== 'undefined' ? (window.__TASCA_NATIVE__ ?? null) : null;

export const isDesktop = native !== null;

/**
 * Converte um `KeyboardEvent.code` no acelerador que o SO entende.
 *
 * As preferências de voz guardam `code` (`Space`, `KeyV`, `F13`) porque é o
 * que não depende do layout do teclado; o registo global quer a notação do
 * Tauri (`Space`, `V`, `F13`). Um mapeamento incompleto falha de forma
 * visível — `bind` devolve `false` — em vez de registar a tecla errada.
 */
export function codeToAccelerator(code: string): string | null {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  if (/^F([1-9]|1[0-9]|2[0-4])$/.test(code)) return code;
  const named: Record<string, string> = {
    Space: 'Space',
    Backquote: '`',
    Minus: '-',
    Equal: '=',
    BracketLeft: '[',
    BracketRight: ']',
    Backslash: '\\',
    Semicolon: ';',
    Quote: "'",
    Comma: ',',
    Period: '.',
    Slash: '/',
    CapsLock: 'CapsLock',
    Insert: 'Insert',
    Home: 'Home',
    End: 'End',
    PageUp: 'PageUp',
    PageDown: 'PageDown',
    ArrowUp: 'Up',
    ArrowDown: 'Down',
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
  };
  return named[code] ?? null;
}
